// @ts-nocheck
import * as React from "react";
import { ManualShell, Section, Lead, Steps, Note, GoTo, Shot, FlowStrip } from "./manual-shell";
import { supportPhone } from "./common";

// 실제 화면 스크린샷 경로 — npm run manual:shots 로 생성(public/manual-shots/agency/).
const S = (key) => `/manual-shots/agency/${key}.png`;

// 유관기관 담당(agency) 사용자 매뉴얼 — 인앱 가이드.
// 진입점은 기관 대시보드 Topbar 우측 상단의 "사용자 매뉴얼" 버튼(dashboard-agency.tsx).
// 콘텐츠는 agency 의 실제 권한·화면(기관 스코프)에 맞춰 작성했다. agency 는 권역이 없고
// 소속 기관(organization_id)으로만 스코프되므로, 권역 대시보드·AI 자동매칭(COD)·승인·
// 리포트·권역 관리는 다루지 않는다(8장 권한 정책 참고).
// 공용 셸/프리미티브(ManualShell·Section·Steps·Note·GoTo)는 manual-shell.tsx 에서 가져온다.

const TOC = [
  { id: "start",     label: "1. 시작하기" },
  { id: "dashboard", label: "2. 기관 대시보드" },
  { id: "flow",      label: "3. 일상 업무 흐름" },
  { id: "schedule",  label: "4. 일정" },
  { id: "map",       label: "5. 지도" },
  { id: "org",       label: "6. 유관기관" },
  { id: "managers",  label: "7. 케어매니저" },
  { id: "drilldown", label: "8. 어르신·방문 상세 조회" },
  { id: "policy",    label: "9. 권한·기관 스코프 정책" },
  { id: "faq",       label: "10. 자주 묻는 질문 · 문의" },
];

const INTRO = (
  <>
    이 매뉴얼은 유관기관 담당자가 자신의 소속 기관에서 수행하는 운영 업무를 화면 흐름에 맞춰 안내합니다.
    기관 현황 모니터링부터 소속 케어매니저·담당 어르신 조회, 방문 일정·지도 확인까지 다룹니다.
    모든 데이터는 <b>본인 소속 기관으로만</b> 제한되며, 자세한 권한 범위는 9장에서 설명합니다.
  </>
);

function AgencyManual() {
  return (
    <ManualShell
      role="agency"
      topbarTitle="사용자 매뉴얼"
      topbarSub="유관기관 담당 가이드"
      badge="AGENCY GUIDE"
      title="유관기관 담당 사용자 매뉴얼"
      revised="최종 개정 2026-06-18 · 유관기관 담당(agency) 기준"
      intro={INTRO}
      toc={TOC}
    >
      <Section id="start" title="1. 시작하기">
        <Lead>유관기관 담당자는 하나의 <b>기관(organization)</b>을 단위로 활동하는 협력 운영자입니다. 로그인하면 자동으로 기관 대시보드(/dashboard/agency)로 이동합니다.</Lead>
        <Steps items={[
          "로그인 화면에서 발급받은 기관 담당자 계정으로 접속합니다.",
          "좌측 사이드바에서 기관 대시보드·일정·지도·유관기관·케어매니저 화면으로 이동할 수 있습니다.",
          "화면 우측 상단의 검색창으로 어르신·케어매니저·기관을 빠르게 찾을 수 있습니다.",
          "도움이 필요할 때는 기관 대시보드 우측 상단의 '사용자 매뉴얼' 버튼으로 언제든 이 화면에 돌아옵니다.",
        ]} />
        <GoTo links={[{ href: "/dashboard/agency", label: "기관 대시보드 열기" }]} />
      </Section>

      <Section id="dashboard" title="2. 기관 대시보드">
        <Lead>소속 기관의 운영 현황을 한눈에 봅니다. KPI 카드 4종과 기관 분포 미니맵이 표시됩니다.</Lead>
        <Steps items={[
          "상단 기간 선택(24시간/7일/30일/분기)을 바꾸면 '완료 방문' 지표와 추이가 함께 갱신됩니다.",
          "KPI 카드에서 소속 케어매니저·담당 어르신·완료 방문·미작성 방문기록 수를 확인합니다.",
          "'미작성 방문기록' 수치가 높으면 소속 매니저의 기록 작성을 점검합니다.",
          "기관 분포 미니맵에서 어르신(위험/주의/안정)과 매니저 위치를 보고, 점을 클릭해 상세로 이동합니다.",
        ]} />
        <Note>대시보드는 항상 <b>본인 소속 기관</b>으로 고정됩니다. 다른 기관·다른 권역의 데이터는 보이지 않습니다(9장 참고).</Note>
        <Shot src={S("dashboard-agency")} alt="기관 대시보드 화면" caption="기관 대시보드 — KPI 카드 4종과 기관 분포 미니맵 (클릭하면 원본 크기로 열림)" />
        <GoTo links={[{ href: "/dashboard/agency", label: "기관 대시보드 열기" }]} />
      </Section>

      <Section id="flow" title="3. 일상 업무 흐름">
        <Lead>유관기관 담당자가 매일 거치는 화면 순서입니다. 썸네일을 클릭하면 원본 화면이 열립니다.</Lead>
        <FlowStrip steps={[
          { src: S("dashboard-agency"), label: "기관 대시보드" },
          { src: S("schedule"),         label: "일정" },
          { src: S("managers"),         label: "케어매니저" },
          { src: S("organizations"),    label: "유관기관" },
        ]} />
        <Steps items={[
          "아침에 기관 대시보드로 그날의 방문 예정·미작성 기록·위험 어르신 현황을 확인합니다.",
          "일정 화면에서 소속 매니저의 방문 일정을 점검합니다.",
          "케어매니저 화면에서 매니저별 상태(가용·바쁨·과부하)를 확인하고 부하를 가늠합니다.",
          "유관기관 화면에서 협력 기관 정보를 조회합니다.",
        ]} />
      </Section>

      <Section id="schedule" title="4. 일정">
        <Lead>소속 기관의 방문 일정을 날짜별로 조회합니다.</Lead>
        <Steps items={[
          "일정 화면에서 날짜를 이동하며 예정·완료 방문을 확인합니다.",
          "방문 항목을 열어 담당 매니저·어르신·시간을 확인합니다.",
          "방문 누락이나 변경이 의심되면 케어매니저 화면에서 담당자 상태를 함께 확인합니다.",
        ]} />
        <Note>일정의 <b>생성·재배정·취소</b>는 권역 운영(권역 관리자/운영자) 권한입니다. 유관기관 담당자는 일정을 <b>조회</b>합니다.</Note>
        <Shot src={S("schedule")} alt="일정 화면" caption="일정 — 날짜별 방문 일정을 조회합니다." />
        <GoTo links={[{ href: "/schedule", label: "일정 열기" }, { href: "/map", label: "지도에서 보기" }]} />
      </Section>

      <Section id="map" title="5. 지도">
        <Lead>소속 기관의 어르신과 케어매니저 위치를 지도에서 확인합니다.</Lead>
        <Steps items={[
          "지도 화면에서 어르신(위험도 색상)과 매니저(상태) 마커를 봅니다.",
          "마커를 클릭하면 해당 어르신·매니저 상세로 이동합니다.",
          "방문 동선이나 담당 권역 분포를 파악할 때 활용합니다.",
        ]} />
        <GoTo links={[{ href: "/map", label: "지도 열기" }]} />
      </Section>

      <Section id="org" title="6. 유관기관">
        <Lead>협력 유관기관 정보를 조회합니다.</Lead>
        <Steps items={[
          "유관기관 화면에서 기관 목록과 기본 정보를 확인합니다.",
          "기관을 선택해 연락처·소속 매니저 등 세부 정보를 봅니다.",
        ]} />
        <Note>이 화면은 <b>조회 전용</b>입니다. 기관 계정의 생성·수정·삭제는 시스템 관리자(및 자기 권역에 한해 권역 관리자) 권한입니다(9장 참고).</Note>
        <Shot src={S("organizations")} alt="유관기관 화면" caption="유관기관 — 협력 기관 목록과 정보를 조회합니다." />
        <GoTo links={[{ href: "/organizations", label: "유관기관 열기" }]} />
      </Section>

      <Section id="managers" title="7. 케어매니저">
        <Lead>소속 케어매니저의 현황과 정보를 조회합니다.</Lead>
        <Steps items={[
          "케어매니저 화면에서 소속 매니저 목록과 자격·연락처를 확인합니다.",
          "매니저별 담당 어르신 수와 상태(가용·바쁨·과부하)를 보고 업무 부하를 가늠합니다.",
          "매니저를 선택해 상세 정보를 봅니다.",
        ]} />
        <Note>매니저 계정의 등록·수정·삭제는 시스템 관리자 권한입니다. 등록이 필요하면 운영지원으로 요청하세요(9장 참고).</Note>
        <Shot src={S("managers")} alt="케어매니저 화면" caption="케어매니저 — 소속 매니저의 자격·연락처·상태를 조회합니다." />
        <GoTo links={[{ href: "/managers", label: "케어매니저 열기" }]} />
      </Section>

      <Section id="drilldown" title="8. 어르신·방문 상세 조회">
        <Lead>대시보드 미니맵이나 목록에서 개별 어르신으로 들어가 케이스·방문 이력을 봅니다.</Lead>
        <Steps items={[
          "기관 대시보드 미니맵 또는 케어매니저 화면에서 어르신을 선택합니다.",
          "어르신 상세에서 기본 정보·케이스 진행 상황을 확인합니다.",
          "케이스·방문 상세로 드릴다운해 방문 기록(상태·특이사항)을 열람합니다.",
        ]} />
        <Note>유관기관 담당자는 소속 기관 범위의 어르신·방문을 <b>조회</b>합니다. 케이스 단계 변경·매칭 결정·방문 기록 작성은 권역 운영·케어매니저의 업무입니다.</Note>
        <GoTo links={[{ href: "/elderly", label: "어르신 상세" }, { href: "/cases", label: "담당 어르신 목록" }, { href: "/visit", label: "방문 상세" }]} />
      </Section>

      <Section id="policy" title="9. 권한·기관 스코프 정책">
        <Lead>유관기관 담당자가 할 수 있는 일과 할 수 없는 일을 명확히 정리합니다.</Lead>
        <p style={{ margin: "12px 0 6px", fontSize: 13, fontWeight: 800, color: "var(--green-600, #2F8F5B)" }}>할 수 있는 작업</p>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.8 }}>
          <li>본인 소속 기관의 현황(대시보드)·일정·지도 조회</li>
          <li>소속 케어매니저·담당 어르신·방문 기록 조회 및 드릴다운</li>
          <li>유관기관 정보 조회</li>
        </ul>
        <p style={{ margin: "12px 0 6px", fontSize: 13, fontWeight: 800, color: "var(--orange-700)" }}>할 수 없는 작업 (권역 운영·관리자 전용)</p>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.8 }}>
          <li>케어 신청 승인/반려, AI 자동매칭(COD) 결정, 방문 일정 생성·재배정</li>
          <li>권역 대시보드·리포트 열람, 권역 관리</li>
          <li>기관·케어매니저 계정의 생성·수정·삭제, 시스템 설정 변경</li>
        </ul>
        <Note><b>기관 스코프:</b> 모든 데이터는 본인 소속 기관(organization)으로 제한됩니다. 권역 관리자가 권역 단위로 보는 것과 달리, 유관기관 담당자는 <b>다른 기관·다른 권역의 데이터를 볼 수 없습니다.</b></Note>
      </Section>

      <Section id="faq" title="10. 자주 묻는 질문 · 문의">
        <p style={{ margin: "10px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. 권역 대시보드나 승인·리포트 메뉴가 보이지 않습니다.</p>
        <Lead>정상입니다. 유관기관 담당자는 기관 스코프로 활동하므로 권역 전용 화면(권역 대시보드·AI 자동매칭·승인·리포트·권역 관리)은 제공되지 않습니다(9장).</Lead>
        <p style={{ margin: "14px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. 소속 케어매니저를 새로 등록하려면?</p>
        <Lead>매니저 계정 생성은 시스템 관리자 권한입니다. 운영지원으로 요청해 주세요.</Lead>
        <p style={{ margin: "14px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. 방문 일정을 바꿔야 합니다.</p>
        <Lead>일정 조정은 권역 운영(권역 관리자/운영자) 권한입니다. 담당 권역 운영자에게 요청하세요. 유관기관 담당자는 일정을 조회합니다(4장).</Lead>
        <div style={{ marginTop: 18, padding: 16, background: "var(--bg-sub)", borderRadius: 12, fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.8 }}>
          <div style={{ fontWeight: 800, color: "var(--ink-1)", marginBottom: 4 }}>운영지원 문의</div>
          support@care.local · {supportPhone()} (평일 09:00–18:00)
        </div>
      </Section>
    </ManualShell>
  );
}

export { AgencyManual };
